import { ColumnDef } from '@tanstack/react-table'
import { Trash2, Check, UserMinus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useTranslation } from 'react-i18next'
import { formatDateForLocale } from '@/lib/dateUtils'
import { buildIconPath } from '@/lib/imagePaths'

// Helper function to normalize MySQL DATETIME strings to UTC ISO format
// MySQL returns dates as "2026-01-23 09:57:00" (no timezone), but they're stored as UTC
const normalizeUtcDate = (datetime: string | Date | null | undefined): string | null => {
    if (!datetime) return null
    if (datetime instanceof Date) {
        return datetime.toISOString()
    }
    if (typeof datetime === 'string') {
        // Convert MySQL format "2026-01-23 09:57:00" to ISO "2026-01-23T09:57:00Z"
        let dateStr = datetime.includes('T') ? datetime : datetime.replace(' ', 'T')
        
        // If no timezone info, treat as UTC (append 'Z')
        if (!dateStr.includes('Z') && !dateStr.match(/[+-]\d{2}:\d{2}$/)) {
            dateStr = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z'
        }
        
        return dateStr
    }
    return null
}

import type { RequestApproval } from './api'

interface RequestApprovalActionsProps {
    requestApproval: RequestApproval
    onApprove?: (requestApproval: RequestApproval) => void
    onDelete?: (requestApproval: RequestApproval) => void
    onUnassign?: (requestApproval: RequestApproval) => void
}

function RequestApprovalActions({ requestApproval, onApprove, onDelete, onUnassign }: RequestApprovalActionsProps) {
    const { t } = useTranslation()

    // Only show actions if we have a valid requestID
    if (!requestApproval.requestID) {
        return (
            <div className="flex items-center justify-center">
                <span className="text-sm text-muted-foreground">-</span>
            </div>
        )
    }

    return (
        <div className="flex items-center justify-center gap-2">
            {/* Green tick icon */}
            <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    if (onApprove) {
                        onApprove(requestApproval)
                    }
                }}
                title={t('requestApprovals.approve')}
            >
                <Check className="h-4 w-4" />
            </Button>
            {/* Red bin icon */}
            <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-destructive hover:text-destructive"
                onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    if (onDelete) {
                        onDelete(requestApproval)
                    }
                }}
                title={t('requestApprovals.delete')}
            >
                <Trash2 className="h-4 w-4" />
            </Button>
            {/* User minus icon for unassign */}
            {onUnassign && (
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-muted-foreground hover:text-destructive"
                    onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        onUnassign(requestApproval)
                    }}
                    title={t('requestApprovals.unassign')}
                >
                    <UserMinus className="h-4 w-4" />
                </Button>
            )}
        </div>
    )
}

export function createRequestApprovalColumns(
    onApprove?: (requestApproval: RequestApproval) => void,
    onDelete?: (requestApproval: RequestApproval) => void,
    onUnassign?: (requestApproval: RequestApproval) => void,
    t?: (key: string) => string,
    locale: string = 'en-GB'
): ColumnDef<RequestApproval>[] {
    const translate = t || ((key: string) => key)

    return [
        {
            accessorKey: 'objectID',
            header: translate('requestApprovals.objectID'),
            size: 200,
            minSize: 150,
            maxSize: 350,
            cell: ({ row }) => {
                const objectID = row.getValue('objectID') as string | null
                const objectDescription = row.original.objectDescription
                return (
                    <div className="flex flex-col min-w-0" title={[objectID, objectDescription].filter(Boolean).join(' — ') || undefined}>
                        <span className="text-sm font-medium truncate">{objectID || '-'}</span>
                        {objectDescription != null && objectDescription !== '' && (
                            <span className="text-xs text-muted-foreground truncate">{objectDescription}</span>
                        )}
                    </div>
                )
            },
        },
        {
            id: 'objectIcon',
            header: '',
            size: 48,
            minSize: 48,
            maxSize: 48,
            meta: { compact: true },
            cell: ({ row }) => {
                const iconFilename = row.original.iconFilename
                if (!iconFilename) return null
                return (
                    <div className="flex items-center justify-center">
                        <img src={buildIconPath(iconFilename)} alt="" className="h-8 w-8 object-contain" />
                    </div>
                )
            },
        },
        {
            accessorKey: 'requestdatetime',
            header: translate('requestApprovals.requestdatetime'),
            size: 180,
            minSize: 150,
            maxSize: 250,
            cell: ({ row }) => {
                const requestdatetime = row.getValue('requestdatetime') as string | null
                if (!requestdatetime) {
                    return <span className="text-muted-foreground">-</span>
                }
                
                const normalizedDate = normalizeUtcDate(requestdatetime)
                if (!normalizedDate) {
                    return <span className="text-muted-foreground">-</span>
                }
                
                try {
                    return (
                        <div className="text-sm" dir="auto">
                            {formatDateForLocale(normalizedDate, locale, {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                            })}
                        </div>
                    )
                } catch (error) {
                    console.warn('Failed to format date:', error)
                    return <span className="text-muted-foreground">-</span>
                }
            },
        },
        {
            accessorKey: 'userName',
            header: translate('requestApprovals.userName'),
            size: 120,
            minSize: 100,
            maxSize: 150,
            cell: ({ row }) => {
                const userName = row.getValue('userName') as string | null
                return (
                    <div className="text-sm">
                        {userName || '-'}
                    </div>
                )
            },
        },
        {
            accessorKey: 'noObjects',
            header: () => <div className="text-center w-full">{translate('requestApprovals.noObjects')}</div>,
            size: 100,
            minSize: 80,
            maxSize: 120,
            cell: ({ row }) => {
                const requestApproval = row.original
                const noObjects = requestApproval.noObjects
                
                // Display null as '-' instead of 0
                if (noObjects === null || noObjects === undefined) {
                    return (
                        <div className="flex items-center justify-center">
                            <span className="text-sm text-muted-foreground">-</span>
                        </div>
                    )
                }
                
                return (
                    <div className="flex items-center justify-center">
                        <span className="text-sm font-medium">{noObjects}</span>
                    </div>
                )
            },
        },
        {
            id: 'actions',
            header: () => <div className="text-center w-full">{translate('common.actions')}</div>,
            size: 100,
            minSize: 80,
            maxSize: 120,
            enableHiding: false,
            cell: ({ row }) => {
                const requestApproval = row.original
                return (
                    <div className="flex items-center justify-center">
                        <RequestApprovalActions 
                            requestApproval={requestApproval} 
                            onApprove={onApprove}
                            onDelete={onDelete}
                            onUnassign={onUnassign}
                        />
                    </div>
                )
            },
        },
    ]
}
